import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Button, Checkbox, IconButton , InputAdornment } from '@material-ui/core'
import { AttachMoney,Delete  } from '@material-ui/icons'
import AddIcon from '@material-ui/icons/Add'
import { EditContext } from 'Components/Hooks/ContextEdit'
import SetState from 'Components/Hooks/SetState'
import BasicTable from 'Components/UI/Atoms/BasicTable'
import useCustomSnackbar from 'Components/UI/Atoms/SnackBar'
import CancelConfirmButtons from 'Components/UI/Molecules/CancelConfirmButtons'
import InputsWithoutValidate from 'Components/UI/Molecules/InputsWithoutValidate'
import InputsWithValidate from 'Components/UI/Molecules/InputsWithValidate'
import DialogComponent from 'Components/UI/Molecules/Modals/DialogComponent'
import { PAYMENT_ORDER_METHODS, PAYMENT_ORDER_METHODS_BY_ORIGIN } from 'Constants'
import { Form, Formik } from 'formik'
import { interfaces } from 'service/interfaces'
import * as yup from 'yup'

const EMPRESA_ID_BY_LABEL = {
  ECO: 1,
  OEM: 2,
  DOS: 3,
}

const CURRENCIES = [
  { id: 1, label: 'Dolares' },
  { id: 2, label: 'Euros' },
  { id: 3, label: 'Reales' },
]

const toNumber = (value) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}
const buildLocalId = () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const getEmpresaId = (origen) => {
  if (typeof origen === 'number') return origen
  const normalized = String(origen || '')
    .trim()
    .toUpperCase()
  return EMPRESA_ID_BY_LABEL[normalized] || 1
}

const buildMethodNameMap = (metodosPago = []) => {
  return metodosPago.reduce((acc, item) => {
    acc[item.id] = item.tipo
    return acc
  }, {})
}

const normalizeMethodRow = (row = {}, methodNameMap = {}) => {
  const tipo = toNumber(row.tipo)
  return {
    id: row.id ?? row.local_id ?? buildLocalId(),
    tipo,
    metodo: methodNameMap[tipo] || row.metodo || '',
    descripcion: row.detalle || row.descripcion || '',
    importe: toNumber(row.importe),
    origen: row.origen,
    lote: row.lote,
    cupon: row.cupon,
    moneda: row.moneda,
    detalle: row.detalle || row.descripcion || '',
    cheque_propio: row.cheque_propio,
    cheque_tercero: row.cheque_tercero,
  }
}

const getReferenceValue = (row = {}, debitCards = [], creditCards = [], banks = []) => {
  const tipo = toNumber(row?.tipo)
  const origen = toNumber(row?.origen)

  if (tipo === 2) return debitCards.find((card) => toNumber(card.id) === origen)?.label || ''
  if (tipo === 3) return creditCards.find((card) => toNumber(card.id) === origen)?.label || ''
  if (tipo === 6 || tipo === 7)
    return banks.find((bank) => toNumber(bank.id) === origen)?.label || ''
  if (tipo === 8)
    return (
      row?.moneda || CURRENCIES.find((currency) => toNumber(currency.id) === origen)?.label || ''
    )
  if (tipo === 49 || tipo === 50 || tipo === 5) {
    const banco = row?.origen || ''
    const numero = row?.cupon || ''
    return [banco, numero].filter(Boolean).join(' - ')
  }

  return ''
}

const AddPaymentMethod = ({
  open,
  handleClose,
  onSubmit,
  methodOptions,
  debitCards,
  creditCards,
  banks,
  empresaId,
}) => {
  const formikRef = useRef(null)
  const [selectedMethod, setSelectedMethod] = useState(methodOptions[0]?.id || '')
  const [availableChecks, setAvailableChecks] = useState([])
  const [selectedChecks, setSelectedChecks] = useState([])
  const [loadingChecks, setLoadingChecks] = useState(false)

  useEffect(() => {
    setSelectedMethod(methodOptions[0]?.id || '')
    setAvailableChecks([])
    setSelectedChecks([])
  }, [open, methodOptions])

  const validationSchema = yup.object().shape({
    metodo: yup.number().required('Campo obligatorio'),
    importe: yup
      .number()
      .typeError('Debe ser numérico')
      .when('metodo', {
        is: (val) => ![49, 50].includes(Number(val)),
        then: (schema) => schema.moreThan(0, 'Debe ser mayor a 0').required('Campo obligatorio'),
        otherwise: (schema) => schema.nullable(),
      }),
    tarjeta: yup.number().when('metodo', {
      is: (val) => [2, 3].includes(Number(val)),
      then: (schema) => schema.required('Campo obligatorio'),
      otherwise: (schema) => schema.nullable(),
    }),
    lote: yup.string().when('metodo', {
      is: (val) => [2, 3].includes(Number(val)),
      then: (schema) => schema.trim().required('Campo obligatorio'),
      otherwise: (schema) => schema.trim(),
    }),
    cupon: yup.string().when('metodo', {
      is: (val) => [2, 3].includes(Number(val)),
      then: (schema) => schema.trim().required('Campo obligatorio'),
      otherwise: (schema) => schema.trim(),
    }),
    nro_operacion: yup.string().when('metodo', {
      is: (val) => [4, 6, 7].includes(Number(val)),
      then: (schema) => schema.trim().required('Campo obligatorio'),
      otherwise: (schema) => schema.trim(),
    }),
    banco: yup.number().when('metodo', {
      is: (val) => [6, 7].includes(Number(val)),
      then: (schema) => schema.required('Campo obligatorio'),
      otherwise: (schema) => schema.nullable(),
    }),
    moneda: yup.number().when('metodo', {
      is: (val) => Number(val) === 8,
      then: (schema) => schema.required('Campo obligatorio'),
      otherwise: (schema) => schema.nullable(),
    }),
    cotizacion: yup.number().when('metodo', {
      is: (val) => Number(val) === 8,
      then: (schema) => schema.moreThan(0, 'Debe ser mayor a 0').required('Campo obligatorio'),
      otherwise: (schema) => schema.nullable(),
    }),
  })

  useEffect(() => {
    let mounted = true
    const metodo = Number(selectedMethod)

    if (![49, 50].includes(metodo)) {
      setAvailableChecks([])
      setSelectedChecks([])
      return undefined
    }

    const loadChecks = async () => {
      setLoadingChecks(true)
      try {
        const query = `?limit=100&offset=0&orderby=id&order=desc&estado=1&empresa=${Number(empresaId)}`
        const response =
          metodo === 49
            ? await interfaces.accounting.listOwnChecks(query)
            : await interfaces.accounting.listThirPartyChecks(query)
        const rows = Array.isArray(response?.data) ? response.data : []
        if (mounted) setAvailableChecks(rows.filter((item) => item?.pago_id == null))
      } catch (error) {
        if (mounted) setAvailableChecks([])
      } finally {
        if (mounted) setLoadingChecks(false)
      }
    }

    loadChecks()
    return () => {
      mounted = false
    }
  }, [selectedMethod, empresaId])

  const toggleCheck = (id) => {
    setSelectedChecks((prev) => {
      if (prev.includes(id)) return prev.filter((item) => item !== id)
      return [...prev, id]
    })
  }

  const fields = (values, setFieldValue) => {
    const fieldsData = [
      {
        class: 'autocomplete',
        name: 'metodo',
        label: 'Método de pago',
        optionValue: 'id',
        optionLabel: `options["label"]`,
        options: methodOptions,
        value: values.metodo,
        sm: 12,
        xs: 12,
        onChange: (value) => {
          setFieldValue('metodo', value?.id || '')
          setSelectedMethod(value?.id || '')
          setSelectedChecks([])
          setFieldValue('tarjeta', '')
          setFieldValue('lote', '')
          setFieldValue('cupon', '')
          setFieldValue('nro_operacion', '')
          setFieldValue('descripcion', '')
          setFieldValue('banco', banks[0]?.id || '')
          setFieldValue('moneda', CURRENCIES[0]?.id || '')
          setFieldValue('cotizacion', '')
        },
      },
    ]

    const metodo = Number(values.metodo)
    const importeField = {
      class: 'textfield',
      name: 'importe',
      label: 'Importe en pesos',
      type: 'number',
      value: values.importe,
      sm: [51, 60, 1].includes(metodo) ? 12 : 6,
      xs: 12,
    }

    const descripcionField = {
      class: 'textfield',
      name: 'descripcion',
      label: 'Descripción',
      value: values.descripcion,
      sm: [6, 7, 8].includes(metodo) ? 6 : 12,
      xs: 12,
      multiline: true,
      rows: 2,
    }

    if (metodo === 1) {
      fieldsData.push(importeField, descripcionField)
      return fieldsData
    }

    if (metodo === 4) {
      fieldsData.push(
        importeField,
        {
          class: 'textfield',
          name: 'nro_operacion',
          label: 'N° operación',
          type: 'number',
          value: values.nro_operacion,
          sm: 6,
          xs: 12,
        },
        descripcionField
      )
      return fieldsData
    }

    if ([2, 3].includes(metodo)) {
      const options = metodo === 2 ? debitCards : creditCards
      fieldsData.push(
        {
          class: 'autocomplete',
          name: 'tarjeta',
          label: 'Tarjeta',
          optionValue: 'id',
          optionLabel: `options["label"]`,
          options,
          value: values.tarjeta,
          sm: 6,
          xs: 12,
        },
        importeField,
        {
          class: 'textfield',
          name: 'lote',
          label: 'Lote',
          type: 'number',
          value: values.lote,
          sm: 6,
          xs: 12,
        },
        {
          class: 'textfield',
          name: 'cupon',
          label: 'Cupón',
          type: 'number',
          value: values.cupon,
          sm: 6,
          xs: 12,
        },
        descripcionField
      )
      return fieldsData
    }

    if ([6, 7].includes(metodo)) {
      fieldsData.push({
        class: 'autocomplete',
        name: 'banco',
        label: 'Banco',
        optionValue: 'id',
        optionLabel: `options["label"]`,
        options: banks,
        value: values.banco,
        sm: [6, 7].includes(metodo) ? 12 : 6,
        xs: 12,
      })
      fieldsData.push(
        {
          ...importeField,
          sm: [6, 7].includes(metodo) ? 12 : importeField.sm,
        },
        {
          class: 'textfield',
          name: 'nro_operacion',
          label: 'N° operación',
          type: 'number',
          value: values.nro_operacion,
          sm: [6, 7].includes(metodo) ? 12 : 6,
          xs: 12,
        },
        {
          ...descripcionField,
          sm: [6, 7].includes(metodo) ? 12 : descripcionField.sm,
        }
      )
      return fieldsData
    }

    if (metodo === 8) {
      fieldsData.push(
        {
          ...importeField,
          sm: 12,
        },
        {
          class: 'autocomplete',
          name: 'moneda',
          label: 'Moneda',
          optionValue: 'id',
          optionLabel: `options["label"]`,
          options: CURRENCIES,
          value: values.moneda,
          sm: 12,
          xs: 12,
        },
        {
          class: 'textfield',
          name: 'cotizacion',
          label: 'Cotización',
          type: 'number',
          value: values.cotizacion,
          sm: 12,
          xs: 12,
        },
        {
          ...descripcionField,
          sm: 12,
        }
      )
      return fieldsData
    }

    if ([49, 50].includes(metodo)) {
      return fieldsData
    }

    // Fallback para tipos adicionales (ej: 51, 60)
    fieldsData.push(importeField, descripcionField)

    return fieldsData
  }

  return (
    <DialogComponent
      open={open}
      handleclose={handleClose}
      title="Agregar método de pago"
      disableBackdropClick
      disableEscapeKeyDown
      widthLarge="500px"
      disableChildHeight
      children={
        <Formik
          innerRef={formikRef}
          initialValues={{
            metodo: methodOptions[0]?.id || '',
            importe: '',
            tarjeta: '',
            lote: '',
            cupon: '',
            nro_operacion: '',
            descripcion: '',
            banco: banks[0]?.id || '',
            moneda: CURRENCIES[0].id,
            cotizacion: '',
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, helpers) => {
            const metodo = Number(values.metodo)
            if ([49, 50].includes(metodo) && selectedChecks.length === 0) {
              helpers.setSubmitting(false)
              return
            }
            const selectedCheckRows = (availableChecks || []).filter((row) =>
              selectedChecks.includes(row.id)
            )
            const success = await onSubmit(values, selectedCheckRows)
            helpers.setSubmitting(false)
            if (success) handleClose()
          }}
          enableReinitialize
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form>
              <InputsWithValidate
                fields={fields(values, setFieldValue)}
                spacing={2}
                setFieldValue={setFieldValue}
                isSubmitting={isSubmitting}
              />
              {[49, 50].includes(Number(values.metodo)) && (
                <div style={{ marginTop: '1rem' }}>
                  <BasicTable
                    col={[
                      { title: '', style: { width: '40px' } },
                      { title: 'Banco', style: { width: '120px' } },
                      { title: 'N° cheque', style: { width: '120px' } },
                      { title: 'Importe', style: { width: '120px' } },
                    ]}
                    size="small"
                    rows={(availableChecks || []).map((row) => ({
                      select: (
                        <Checkbox
                          checked={selectedChecks.includes(row.id)}
                          onChange={() => toggleCheck(row.id)}
                        />
                      ),
                      banco: row.banco,
                      cheque: row.nro_cheque,
                      importe: `$${toNumber(row.importe).toFixed(2)}`,
                    }))}
                  />
                  <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                    {loadingChecks
                      ? 'Cargando cheques en cartera...'
                      : `Cheques seleccionados: ${selectedChecks.length}`}
                  </div>
                  {!loadingChecks && availableChecks.length === 0 && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                      No hay cheques en cartera disponibles para este origen.
                    </div>
                  )}
                  {!loadingChecks && selectedChecks.length === 0 && availableChecks.length > 0 && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                      Seleccioná al menos un cheque para continuar.
                    </div>
                  )}
                </div>
              )}
            </Form>
          )}
        </Formik>
      }
      actions={
        <CancelConfirmButtons
          disabledSubmit={[49, 50].includes(Number(selectedMethod)) && selectedChecks.length === 0}
          onClickSubmit={() => {
            if (formikRef.current) formikRef.current.submitForm()
          }}
          onClickCancel={handleClose}
          textButtonSubmit="CONFIRMAR"
        />
      }
    />
  )
}

const EditPaymentMethod = ({ open, handleClose, row, onSubmit }) => {
  const formikRef = useRef(null)

  return (
    <DialogComponent
      open={open}
      handleclose={handleClose}
      title="Editar método de pago"
      disableBackdropClick
      disableEscapeKeyDown
      widthLarge="500px"
      disableChildHeight
      children={
        <Formik
          innerRef={formikRef}
          initialValues={{
            descripcion: row?.descripcion || '',
            importe: row?.importe || 0,
          }}
          enableReinitialize
          validationSchema={yup.object().shape({
            importe: yup
              .number()
              .typeError('Debe ser numérico')
              .moreThan(0, 'Debe ser mayor a 0')
              .required('Campo obligatorio'),
            descripcion: yup.string().trim(),
          })}
          onSubmit={async (values, helpers) => {
            const success = await onSubmit(values)
            helpers.setSubmitting(false)
            if (success) handleClose()
          }}
        >
          {({ values }) => (
            <Form>
              <InputsWithValidate
                fields={[
                  {
                    class: 'textfield',
                    name: 'descripcion',
                    label: 'Descripción',
                    value: values.descripcion,
                    sm: 12,
                    xs: 12,
                    multiline: true,
                    rows: 2,
                  },
                  {
                    class: 'textfield',
                    name: 'importe',
                    label: 'Importe en pesos',
                    type: 'number',
                    value: values.importe,
                    sm: 12,
                    xs: 12,
                  },
                ]}
                spacing={2}
              />
            </Form>
          )}
        </Formik>
      }
      actions={
        <CancelConfirmButtons
          onClickSubmit={() => {
            if (formikRef.current) formikRef.current.submitForm()
          }}
          onClickCancel={handleClose}
          textButtonSubmit="CONFIRMAR"
        />
      }
    />
  )
}

const PaymentMethodContent = (props) => {
  const { data, originalData, setData, formikRef } = props

  const [edit] = useContext(EditContext)
  const createDismissableSnackbar = useCustomSnackbar()
  const isRegistered = originalData?.paymentOrder?.estado === 'Registrado'

  const { state: add, setOpen: openAdd, setClose: closeAdd } = SetState()
  const { state: editModal, setOpen: openEdit, setClose: closeEdit } = SetState()

  const [selectedRow, setSelectedRow] = useState(null)
  const [beforeData, setBeforeData] = useState(null)

  const empresaId = getEmpresaId(data?.paymentOrder?.origen)
  const originLabel = String(data?.paymentOrder?.origen || '')
    .trim()
    .toUpperCase()
  const allowedMethodIds =
    PAYMENT_ORDER_METHODS_BY_ORIGIN[originLabel] || PAYMENT_ORDER_METHODS_BY_ORIGIN.ECO
  const orderId = toNumber(data?.paymentOrder?.id)
  const methodNameMap = useMemo(
    () => buildMethodNameMap(beforeData?.metodosPago || []),
    [beforeData]
  )

  const [rows, setRows] = useState(() =>
    (data?.paymentOrderDetails || []).map((row) => normalizeMethodRow(row, methodNameMap))
  )
  useEffect(() => {
    interfaces.salesPaymentOrders
      .getBeforeData()
      .then((res) => {
        setBeforeData(res?.data || null)
      })
      .catch(() => {
        setBeforeData(null)
      })
  }, [])

  useEffect(() => {
    setRows((data?.paymentOrderDetails || []).map((row) => normalizeMethodRow(row, methodNameMap)))
  }, [data?.paymentOrderDetails, methodNameMap])

  const methodOptions = useMemo(() => {
    const options = (beforeData?.metodosPago || [])
      .filter((item) => allowedMethodIds.includes(item.id))
      .map((item) => ({ id: item.id, label: item.tipo }))

    if (options.length > 0) return options

    return PAYMENT_ORDER_METHODS.filter((item) => allowedMethodIds.includes(item.id)).map(
      (item) => ({
        id: item.id,
        label: item.tipo,
      })
    )
  }, [beforeData, allowedMethodIds])

  const debitCards = useMemo(
    () =>
      (beforeData?.tarjetas || [])
        .filter((item) => item.tipo === 1)
        .map((item) => ({ id: item.id, label: item.nombre })),
    [beforeData]
  )

  const creditCards = useMemo(
    () =>
      (beforeData?.tarjetas || [])
        .filter((item) => item.tipo === 2)
        .map((item) => ({ id: item.id, label: item.nombre })),
    [beforeData]
  )

  const banks = useMemo(
    () => (beforeData?.bancos || []).map((item) => ({ id: item.id, label: item.nombre })),
    [beforeData]
  )

  const total = useMemo(
    () => (rows || []).reduce((acc, row) => acc + toNumber(row.importe), 0),
    [rows]
  )

  const updateDraftRows = (nextRows) => {
    setRows(nextRows)
    const totalImporte = nextRows.reduce((acc, row) => acc + toNumber(row.importe), 0)
    setData((prev) => ({
      ...prev,
      paymentOrderDetails: nextRows,
      paymentOrder: {
        ...(prev?.paymentOrder || {}),
        total: totalImporte,
      },
      info: {
        ...(prev?.info || {}),
        importe_total: totalImporte,
      },
    }))
  }

  const handleDelete = async (rowId) => {
    if (!edit || isRegistered || !orderId || !rowId) return

    const nextRows = (rows || []).filter((row) => row.id !== rowId)
    updateDraftRows(nextRows)
    createDismissableSnackbar('Item eliminado del borrador', { variant: 'info' })
  }

  const buildDraftRowsFromAdd = (values, checks = []) => {
    const tipo = Number(values.metodo)
    const metodoLabel = methodOptions.find((item) => Number(item.id) === tipo)?.label || ''
    const common = {
      id: buildLocalId(),
      tipo,
      metodo: metodoLabel,
      importe: toNumber(values.importe),
      descripcion: values.descripcion || '',
      detalle: values.descripcion || '',
      origen: 0,
      lote: null,
      cupon: null,
      moneda: '',
    }

    if ([2, 3].includes(tipo)) {
      common.origen = toNumber(values.tarjeta)
      common.lote = toNumber(values.lote)
      common.cupon = toNumber(values.cupon)
    }

    if (tipo === 4) {
      common.cupon = toNumber(values.nro_operacion)
    }

    if ([6, 7].includes(tipo)) {
      common.origen = toNumber(values.banco)
      common.cupon = toNumber(values.nro_operacion)
    }

    if (tipo === 8) {
      const currency = CURRENCIES.find((item) => item.id === Number(values.moneda))
      common.origen = toNumber(values.moneda)
      common.moneda = currency?.label || ''
      common.cupon = toNumber(values.cotizacion)
    }

    if (tipo === 49) {
      return (checks || []).map((check) => ({
        ...common,
        id: buildLocalId(),
        cheque_propio: check.id,
        origen: check.banco,
        cupon: check.nro_cheque,
        importe: toNumber(check.importe),
      }))
    }

    if (tipo === 50) {
      return (checks || []).map((check) => ({
        ...common,
        id: buildLocalId(),
        cheque_tercero: check.id,
        origen: check.banco,
        cupon: check.nro_cheque,
        importe: toNumber(check.importe),
      }))
    }

    return [common]
  }

  const addItem = async (values, checks = []) => {
    if (!edit || isRegistered || !orderId) return false

    const newRows = buildDraftRowsFromAdd(values, checks)
    updateDraftRows([...(rows || []), ...newRows])
    createDismissableSnackbar('Item agregado al borrador', { variant: 'info' })
    return true
  }

  const editItem = async (values) => {
    if (!selectedRow?.id || isRegistered || !orderId) return false

    const nextRows = (rows || []).map((row) =>
      row.id === selectedRow.id
        ? {
            ...row,
            importe: toNumber(values.importe),
            descripcion: values.descripcion || '',
            detalle: values.descripcion || '',
          }
        : row
    )
    updateDraftRows(nextRows)
    createDismissableSnackbar('Item actualizado en borrador', { variant: 'info' })
    return true
  }

  const tableRows = (rows || []).map((row) => ({
    metodo: row.metodo || methodNameMap[row.tipo] || '',
    referencia: getReferenceValue(row, debitCards, creditCards, banks),
    descripcion: row.descripcion || '',
    monto: `$${toNumber(row.importe).toFixed(2)}`,
    action: (
      <IconButton
        onClick={(event) => {
          event.stopPropagation()
          handleDelete(row.id)
        }}
        disabled={!edit || isRegistered}
        style={{ padding: '0px' }}
      >
        <Delete />
      </IconButton>
    ),
    id: row.id,
  }))

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      initialValues={{ _dummy: '' }}
      validationSchema={yup.object().shape({ _dummy: yup.mixed() })}
      onSubmit={() => {
        updateDraftRows(rows || [])
      }}
    >
      {() => (
        <Form>
          <div style={{ margin: '2rem' }}>
            <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'flex-start' }}>
              <InputsWithoutValidate
                fields={[
                  {
                    class: 'textfield',
                    name: 'importe_total',
                    label: 'Importe total',
                    value: total.toFixed(2),
                    disabled: true,
                    sm: 12,
                    xs: 12,
                    inputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoney />
                        </InputAdornment>
                      ),
                    },
                  },
                ]}
                spacing={2}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <BasicTable
                col={[
                  { title: 'Medio de pago', style: { width: '180px' } },
                  { title: 'Referencia', style: { width: '180px' } },
                  { title: 'Descripción', style: { width: '220px' } },
                  { title: 'Importe en pesos', style: { width: '150px' } },
                  { title: '', style: { width: '40px' } },
                  { title: '', style: { display: 'none' } },
                ]}
                size="small"
                rows={tableRows}
                onClickRow={(row) => {
                  if (!edit || isRegistered) return
                  const selected = rows.find((item) => item.id === row.id)
                  setSelectedRow(selected || null)
                  openEdit(true)
                }}
              />

              <div style={{ marginLeft: '1rem' }}>
                <Button
                  onClick={openAdd}
                  disabled={!edit || isRegistered}
                  color="primary"
                  variant="contained"
                  size="large"
                  style={{ height: '40px' }}
                >
                  <AddIcon />
                </Button>
              </div>
            </div>
          </div>
          {add.suspense && (
            <AddPaymentMethod
              open={add.open}
              handleClose={closeAdd}
              onSubmit={addItem}
              methodOptions={methodOptions}
              debitCards={debitCards}
              creditCards={creditCards}
              banks={banks}
              empresaId={empresaId}
            />
          )}

          {editModal.suspense && (
            <EditPaymentMethod
              open={editModal.open}
              handleClose={closeEdit}
              row={selectedRow}
              onSubmit={editItem}
            />
          )}
        </Form>
      )}
    </Formik>
  )
}

export default PaymentMethodContent
